<template>
  <header class="header" ref="header">
    <div class="container">
      <div>
        <button class="header__MenuButton" :class="{isActive: menuOpen}" @click="toggleMenu" aria-label="Main menu">
          <span />
        </button>

        <nav class="header__Links">
          <ul>
            <li v-for="item in aggregationPagesLinks">
              <NuxtLink :to="item.sef">{{ $t(`${ item.isHotel ? 'hotelsAndResorts' : 'campsites' }`) }}</NuxtLink>
            </li>
          </ul>
        </nav>
      </div>

      <nuxt-link :to="link('Home')" class="header__Logo" aria-label="Aminess">
        <Icon name="logo" />
      </nuxt-link>

      <div class="header__RightSide" v-click-outside="() => closeModals()">
        <div class="header__RightSideButtons">
          <button :aria-label="$t('contact')" @click="open('contact')">
            <Icon name="phone" />
            <span>{{ $t('header.contact') }}</span>
          </button>
          <button v-if="!userData" :aria-label="$t('userProfile')" @click="open('login')">
            <Icon name="profile" />
            <span>{{ $t('header.login') }}</span>
          </button>
          <button v-else :aria-label="$t('userProfile')" @click="toggleModal('loggedUser')" class="header__UserLoggedButton">
            {{ userInitials }}
          </button>
        </div>

        <div class="header__ModalsWrapper">
          <div class="container">
            <transition name="fade" mode="out-in">
              <div v-if="modals.loggedUser && userData?.profile" class="header__Modal userLoggedModal">
                <div class="userLoggedModal__Top">
                  <span>{{ userData.profile.first_name }} {{ userData.profile.last_name }}</span>
                  {{ $t('amiPointsPlural', userData.loyalty.cumulative_points) }}
                </div>

                <div class="userLoggedModal__Links">
                  <nuxt-link v-for="item in loggedModalLinks" :to="link(item.linkId)">
                    <Icon :name="item.iconId" />
                    <span>{{ $t(`loyalty.${ item.labelId }`) }}</span>
                  </nuxt-link>

                  <button @click="logoutUser" class="userLoggedModal__Logout">
                    <Icon name="logout" />
                    <span>{{ $t('header.logout') }}</span>
                  </button>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <Modal v-show="dialogs.login" v-model="dialogs.login" class="modal__Login">
        <p class="modal__Content--Title" v-text="$t('header.sign')" />
        <Button @click="continueToLogin" full-width medium>{{ $t('header.login') }}</Button>
  
        <div>
          <p class="modal__Content--Title" v-text="$t('header.loyaltyTitle')" />
          <p v-text="$t('header.loyaltyDescription')" />
          <Button type="nuxt-link" :to="link('Registration')" secondary full-width medium>{{ $t('header.loyaltyButton') }}</Button>
        </div>
      </Modal>

      <Modal v-show="dialogs.contact" v-model="dialogs.contact" class="modal__Contact">
        <ContactDropdown @closeContactDropdown="close('contact')" />
      </Modal>
    </Teleport>
  </header>

  <HeaderNavigation />
</template>

<script setup>
  const { link, links, aggregationPagesLinks } = useSetup();
  const { t } = useI18n();
  const { template } = usePage();
  const { path } = useRoute();
  const { user: userData, logout } = useAuth('aminess');
  const header = ref();
  const scrollTimeout = ref(null);
  const lastScrollY = ref(0);
  const menuOpen = useState('menuOpen', () => false);
  const booking = useState('booking');
  const modalsDefaultValues = {
    contact: false,
    loggedUser: false,
  }
  const modals = ref({ ...modalsDefaultValues });

  const dialogs = reactive({
    login: false,
    contact: false
  });
  const { open, close } = useModal(dialogs);
  const loggedModalLinks = [
    { labelId: 'points', linkId: 'Points', iconId: 'star-outline' },
    { labelId: 'stays', linkId: 'Stays', iconId: 'hotel' },
    { labelId: 'profile', linkId: 'Profile', iconId: 'user' },
    { labelId: 'faq', linkId: 'LoyaltyFAQ', iconId: 'faq' },
    { labelId: 'terms', linkId: 'LoyaltyTerms', iconId: 'terms' },
  ];

  const userInitials = computed(() => {
    if (userData.value?.profile) {
      const {first_name, last_name} = userData.value.profile;
      try {
        return `${first_name[0]}${last_name[0]}`;
      } catch(error) {}
    }
    return '';
  });

  function toggleMenu() {
    menuOpen.value = !menuOpen.value;
    document.documentElement.classList.add('isMenuOpen');
  }

  function toggleModal(modal) {
    modals.value[modal] = !modals.value[modal];
    for (let item of Object.keys(modals.value)) {
      if (item !== modal) modals.value[item] = false;
    }
  }
  
  function closeModals() {
    modals.value = { ...modalsDefaultValues }
  }

  function detectHeaderPosition() {    
    if (window.innerWidth <= 768) {
      clearTimeout(scrollTimeout.value);
      scrollTimeout.value = setTimeout(() => {
        closeModals();
        if (header.value) {
          if (window.scrollY > header.value.clientHeight && window.scrollY > lastScrollY.value) {
            header.value.classList.add('header--Translate');
          } else {
            header.value.classList.remove('header--Translate');
          }
        }
        lastScrollY.value = window.scrollY;
      }, 50);
    }
  }

  async function logoutUser() {
    await logout();
  }

  async function continueToLogin() {
    if (template.value === 'Login') {
      close('login');
      return;
    }
    await navigateTo(link('Login'));
  }

  async function goToAccommodationsPage(link, category) {
    if (booking?.value?.accommodation) booking.value.accommodation = { id: category, name: t(`reservation.all.${category}`) }
    await navigateTo(link);
  }

  onMounted(() => {
    lastScrollY.value = window.scrollY;
    window.addEventListener('scroll', detectHeaderPosition);
  })

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', detectHeaderPosition);
  })
</script>

<style lang="scss">
  @import url('@/assets/styles/components/_header.scss');
</style>